import { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Textarea } from '@/shared/ui/textarea';
import { Label } from '@/shared/ui/label';
import { Plus, ExternalLink, Eye, Trash2, Upload, MousePointerClick, X, RotateCcw, Maximize2, Minimize2, Copy } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/shared/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip';
import { VRTour, useVRTours } from '@/features/properties/hooks/useVRTours';
import { VRTourViewer } from './VRTourViewer';
import { MvpLockedPopup } from '@/shared/components/mvp/MvpLockedPopup';
import { cn } from '@/shared/lib/utils';
import { toast } from 'sonner';
import { useIsDemoUser } from '@/shared/hooks/useIsDemoUser';

interface VRTourManagerProps {
  propertyId: string;
  className?: string;
}

const isMatterportTour = (url: string) => url.includes('matterport.com');

/** Non-Matterport links (e.g. DroneDeploy) render as a generic "Additional Viewer". */
const tourTitle = (tour: VRTour) =>
  isMatterportTour(tour.link_url) ? (tour.link_name || 'Matterport Tour') : 'Additional Viewer';

// play=1: auto-start, qs=1: quickstart, help=0: hide help, brand=0: hide branding
// Do not add mt=0 or mls — they hide Mattertags / strip tag content
const buildEmbedUrl = (url: string) =>
  isMatterportTour(url) ? `${url}${url.includes('?') ? '&' : '?'}play=1&qs=1&help=0&brand=0` : url;

// Viewport-fitting grid shapes for 1–4 tours; 5+ falls back to a scrolling two-column grid.
const GRID_LAYOUTS: Record<number, string> = {
  1: 'grid-cols-1 grid-rows-1',
  2: 'grid-cols-2 grid-rows-1',
  3: 'grid-cols-2 grid-rows-2', // first tour spans the top row, two below
  4: 'grid-cols-2 grid-rows-2',
};

export function VRTourManager({ propertyId, className }: VRTourManagerProps) {
  const { tours, loading, deleteTour, incrementViewCount } = useVRTours(propertyId);
  const isDemoUser = useIsDemoUser();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [mvpLockedOpen, setMvpLockedOpen] = useState(false);
  const [selectedTour] = useState<VRTour | null>(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [activeTourId, setActiveTourId] = useState<string | null>(null);
  const iframeRefs = useRef<Record<string, HTMLIFrameElement | null>>({});
  const containerRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [fullscreenTourId, setFullscreenTourId] = useState<string | null>(null);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setFullscreenTourId(null);
      }
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handleResetTour = (tourId: string) => {
    const iframe = iframeRefs.current[tourId];
    if (iframe) {
      // Re-setting src forces the cross-origin iframe to reload
      const { src } = iframe;
      iframe.src = src;
    }
  };

  const handleToggleFullscreen = (tourId: string) => {
    const container = containerRefs.current[tourId];
    if (!container) return;
    if (fullscreenTourId === tourId) {
      document.exitFullscreen?.();
    } else {
      container.requestFullscreen?.();
      setFullscreenTourId(tourId);
    }
  };

  const handleCopyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const [formData, setFormData] = useState({
    tour_name: '',
    tour_description: '',
    matterport_model_id: '',
    link_url: '',
    thumbnail_url: '',
  });

  const handleCreateTour = async (e: React.FormEvent) => {
    e.preventDefault();
    // MVP locked: show popup instead of creating tour (uncomment block below to restore)
    setMvpLockedOpen(true);
    // try {
    //   await createTour(formData);
    //   setFormData({
    //     tour_name: '',
    //     tour_description: '',
    //     matterport_model_id: '',
    //     link_url: '',
    //     thumbnail_url: '',
    //   });
    //   setIsCreateDialogOpen(false);
    // } catch (error) {
    //   console.error('Error creating tour:', error);
    // }
  };

  const handleDeleteTour = async (tourId: string) => {
    if (confirm('Are you sure you want to delete this VR tour?')) {
      await deleteTour(tourId);
    }
  };

  if (loading) {
    return (
      <div className={cn("space-y-4", className)}>
        {[...Array(3)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader>
              <div className="h-6 bg-muted rounded w-1/3"></div>
              <div className="h-4 bg-muted rounded w-2/3"></div>
            </CardHeader>
            <CardContent>
              <div className="aspect-video bg-muted rounded"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const fitsViewport = tours.length <= 4;

  return (
    <div className={cn("flex flex-col gap-4", fitsViewport && "h-full", className)}>
      {/* Header with Create Button */}
      <div className="flex items-center justify-between shrink-0">
        <div>
          <h2 className="text-xl font-bold">Virtual Reality Tours</h2>
          <p className="text-sm text-muted-foreground">
            Manage immersive VR experiences for this property
          </p>
        </div>

        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2">
              <Plus className="h-4 w-4" />
              Add VR Tour
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>Add New VR Tour</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateTour} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="tour_name">Tour Name</Label>
                <Input
                  id="tour_name"
                  value={formData.tour_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, tour_name: e.target.value }))}
                  placeholder="Main Lobby VR Tour"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tour_description">Description</Label>
                <Textarea
                  id="tour_description"
                  value={formData.tour_description}
                  onChange={(e) => setFormData(prev => ({ ...prev, tour_description: e.target.value }))}
                  placeholder="A detailed virtual tour of the main lobby area..."
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="matterport_model_id">Matterport Model ID</Label>
                <Input
                  id="matterport_model_id"
                  value={formData.matterport_model_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, matterport_model_id: e.target.value }))}
                  placeholder="d1f3f1e2-123a-456b-789c-012345678901"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="link_url">Tour URL</Label>
                <Input
                  id="link_url"
                  type="url"
                  value={formData.link_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, link_url: e.target.value }))}
                  placeholder="https://my.matterport.com/show/?m=..."
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="thumbnail_url">Thumbnail URL (optional)</Label>
                <Input
                  id="thumbnail_url"
                  type="url"
                  value={formData.thumbnail_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, thumbnail_url: e.target.value }))}
                  placeholder="https://..."
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="gap-2">
                  <Upload className="h-4 w-4" />
                  Create VR Tour
                </Button>
              </div>
            </form>
            <MvpLockedPopup
              open={mvpLockedOpen}
              onOpenChange={setMvpLockedOpen}
              title="MVP locked"
              message="Creating VR tours will be available soon."
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Tours Grid */}
      {tours.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <div className="w-16 h-16 mx-auto mb-4 bg-muted rounded-full flex items-center justify-center">
              <Eye className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No VR Tours Yet</h3>
            <p className="text-muted-foreground mb-4">
              Create your first virtual reality tour to showcase this property in an immersive way.
            </p>
            <Button onClick={() => setIsCreateDialogOpen(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              Add Your First VR Tour
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div
          className={cn(
            "grid gap-4",
            fitsViewport ? cn("flex-1 min-h-0", GRID_LAYOUTS[tours.length]) : "grid-cols-2"
          )}
        >
          {tours.map((tour, index) => (
            <Card
              key={tour.id}
              className={cn(
                "overflow-hidden",
                fitsViewport && "flex flex-col min-h-0",
                tours.length === 3 && index === 0 && "col-span-2"
              )}
            >
              <CardHeader className="py-3 shrink-0">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      {tourTitle(tour).replace(/^./, c => c.toUpperCase())}
                    </CardTitle>
                    {tour.link_metadata?.tour_description && (
                      <CardDescription>{tour.link_metadata.tour_description}</CardDescription>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="icon"
                          aria-label="Reset"
                          onClick={() => handleResetTour(tour.id)}
                        >
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Reset</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="icon"
                          aria-label={fullscreenTourId === tour.id ? 'Exit Fullscreen' : 'Fullscreen'}
                          onClick={() => handleToggleFullscreen(tour.id)}
                        >
                          {fullscreenTourId === tour.id ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{fullscreenTourId === tour.id ? 'Exit Fullscreen' : 'Fullscreen'}</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="icon"
                          aria-label="Copy Link"
                          onClick={() => handleCopyLink(tour.link_url)}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Copy Link</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="icon"
                          aria-label="Open in New Tab"
                          onClick={() => window.open(tour.link_url, '_blank')}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Open in New Tab</TooltipContent>
                    </Tooltip>
                    {!isDemoUser && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="icon"
                            aria-label="Delete Tour"
                            onClick={() => handleDeleteTour(tour.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Delete Tour</TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </div>
              </CardHeader>

              <CardContent className={cn("p-0", fitsViewport && "flex-1 flex flex-col min-h-0")}>
                {/* Instant playback - embedded viewer iframe with click-to-activate overlay */}
                <div
                  ref={(el) => { containerRefs.current[tour.id] = el; }}
                  className={cn(
                    "relative bg-muted overflow-hidden",
                    fitsViewport ? "flex-1 min-h-0" : "h-[400px]"
                  )}
                >
                  <iframe
                    ref={(el) => { iframeRefs.current[tour.id] = el; }}
                    src={buildEmbedUrl(tour.link_url)}
                    className="w-full h-full border-0"
                    title={tourTitle(tour)}
                    allow="vr; xr; accelerometer; gyroscope"
                    style={{ pointerEvents: activeTourId === tour.id ? 'auto' : 'none' }}
                  />
                  {/* Click-to-activate overlay — blocks iframe from capturing scroll */}
                  {activeTourId !== tour.id && (
                    <div
                      className="absolute inset-0 cursor-pointer flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors"
                      onClick={() => setActiveTourId(tour.id)}
                    >
                      <div className="flex items-center gap-2 bg-black/70 text-white px-4 py-2 rounded-lg text-sm font-medium backdrop-blur-sm">
                        <MousePointerClick className="h-4 w-4" />
                        Click to interact
                      </div>
                    </div>
                  )}
                  {/* Stop interacting button — lets user regain page scroll */}
                  {activeTourId === tour.id && (
                    <Button
                      variant="secondary"
                      size="sm"
                      className="absolute top-3 right-3 gap-1.5 bg-black/70 text-white hover:bg-black/90 border-0 backdrop-blur-sm z-10"
                      onClick={() => setActiveTourId(null)}
                    >
                      <X className="h-3.5 w-3.5" />
                      Stop Interacting
                    </Button>
                  )}
                </div>

                <div className="p-3 bg-muted/50 border-t shrink-0">
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    {isMatterportTour(tour.link_url) && (
                      <span>Model ID: {tour.link_metadata?.matterport_model_id || 'N/A'}</span>
                    )}
                    <span className="ml-auto">Created: {new Date(tour.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* VR Tour Viewer Dialog */}
      <Dialog open={isViewerOpen} onOpenChange={setIsViewerOpen}>
        <DialogContent className="max-w-7xl h-[90vh] flex flex-col p-0">
          <DialogHeader className="p-6 pb-4">
            <DialogTitle>VR Tour Viewer</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden px-6 pb-6">
            {selectedTour && (
              <VRTourViewer
                tour={selectedTour}
                onViewCountIncrement={incrementViewCount}
                className="h-full"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}